/**
 * Появление подсказки `micro_feedback` после ответа — задание
 * stylepack/feedback-reveal (слово владельца 21.10.2026, Blinkist: под
 * вариантами после выбора выезжает карточка-подсказка, до выбора её нет).
 *
 * Одна дверь правила «видна ли подсказка»: отрисовка блока
 * (`PublicBlockRendererExtended`), визард квиза (что отдать рендеру) и панель
 * (какие варианты предложить) берут его отсюда. Зеркало полей схемы сервера
 * (`backend/src/ai-funnels/block-schema/micro-feedback-reveal.ts`) —
 * `MICRO_FEEDBACK_REVEAL_FIELDS`, сверяется сторожем концов
 * `micro-feedback-reveal.ends.spec.ts`.
 *
 *  - `revealAfterAnswer: true` — скрыта, пока в блоке выбора ЭТОГО экрана
 *    (single_choice / multiple_choice / image_choice) не отмечен вариант;
 *  - `revealOptionIds` — только для этих вариантов (пусто — для любого);
 *    непустой перечень включает появление и без флажка;
 *  - полноэкранный вид (`feedbackLayout: 'fullscreen'`) полей не судит.
 *
 * Ответы экрана приходят той же дверью, что у «Условий оплаты»: блоки экрана
 * (`screenBlocks`) и словарь ответов `FormValuesContext` (ключ — id блока,
 * у множественного выбора и картинок — id через запятую). Блоков экрана нет
 * (рендер вне квиза) — прежнее поведение: подсказка видна.
 *
 * Появление — приём кита `Reveal` (0fr → 1fr по высоте и прозрачность, та же
 * кривая и длительность, при «уменьшить движение» сразу; свёрнутая не занимает
 * места, `aria-hidden` и `inert`). Свой узел, а не сам `Reveal`: покупателю
 * нужен признак `data-revealed` на той же обёртке. В превью конструктора
 * подсказка видна всегда, с пометкой автору.
 */
import React from 'react';
import type { EditorBlock } from '@/store/editor/types/blocks';
import {
  REVEAL_DURATION_MS,
  REVEAL_EASING,
  usePrefersReducedMotion,
} from '@/components/kit/Reveal';
import { pickStrings } from '@/i18n';
import { authorHintsStrings } from '@/i18n/dictionaries/authorHints';
import { richFieldToPlainText, type RichField } from '@/types/rich-text';

/** Зеркало полей схемы сервера (сторож концов читает этот перечень). */
export const MICRO_FEEDBACK_REVEAL_FIELDS = [
  'revealAfterAnswer',
  'revealOptionIds',
] as const;

/** Блоки выбора, чей отмеченный вариант открывает подсказку. */
export const REVEAL_CHOICE_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'single_choice',
  'multiple_choice',
  'image_choice',
]);

/** Включено ли у блока появление после ответа. */
export function microFeedbackRevealGated(block: EditorBlock): boolean {
  if (block.type !== 'micro_feedback') return false;
  if (block.feedbackLayout === 'fullscreen') return false;
  return (
    block.revealAfterAnswer === true ||
    (block.revealOptionIds?.filter(Boolean).length ?? 0) > 0
  );
}

/** Отмеченные варианты всех блоков выбора экрана. */
export function selectedChoiceIds(
  screenBlocks: readonly EditorBlock[],
  values: Record<string, string>,
): Set<string> {
  const out = new Set<string>();
  for (const b of screenBlocks) {
    if (!REVEAL_CHOICE_BLOCK_TYPES.has(b.type)) continue;
    const raw = values[b.id];
    if (typeof raw !== 'string') continue;
    for (const id of raw.split(',')) if (id) out.add(id);
  }
  return out;
}

/** Видна ли подсказка покупателю при текущих ответах экрана. */
export function isMicroFeedbackRevealed(
  block: EditorBlock,
  screenBlocks: readonly EditorBlock[] | undefined,
  values: Record<string, string>,
): boolean {
  if (!microFeedbackRevealGated(block) || !screenBlocks) return true;
  const selected = selectedChoiceIds(screenBlocks, values);
  if (selected.size === 0) return false;
  const only = block.revealOptionIds?.filter(Boolean) ?? [];
  return only.length === 0 || only.some((id) => selected.has(id));
}

/**
 * Что визард квиза отдаёт рендеру блока: блоки экрана — только подсказке с
 * появлением, прочим ничего (их вход и memo не меняются).
 */
export function microFeedbackRevealProps(
  block: EditorBlock,
  visibleBlocks: EditorBlock[],
): { screenBlocks?: EditorBlock[] } {
  return microFeedbackRevealGated(block) ? { screenBlocks: visibleBlocks } : {};
}

/** Варианты блоков выбора экрана — для выбора «Только для ответов» в панели. */
export function revealChoiceOptions(
  screenBlocks: readonly EditorBlock[],
): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  for (const b of screenBlocks) {
    if (!REVEAL_CHOICE_BLOCK_TYPES.has(b.type)) continue;
    // У картинок варианты в своём поле (шрам WEB-1301), старые — в options.
    const list =
      (b.type === 'image_choice' ? b.imageChoiceOptions : undefined) ??
      b.options ??
      [];
    for (const o of list as { id?: string; label?: unknown }[]) {
      if (typeof o?.id !== 'string' || !o.id) continue;
      const label = richFieldToPlainText(o.label as RichField).trim();
      out.push({ value: o.id, label: label || o.id });
    }
  }
  return out;
}

export interface MicroFeedbackRevealProps {
  block: EditorBlock;
  screenBlocks: readonly EditorBlock[] | undefined;
  values: Record<string, string>;
  /** Превью конструктора: видна всегда, с пометкой. */
  editor: boolean;
  children: React.ReactElement;
}

/** Обёртка появления плашки; без появления — дети как есть. */
export function MicroFeedbackReveal({
  block,
  screenBlocks,
  values,
  editor,
  children,
}: MicroFeedbackRevealProps): React.ReactElement {
  const reducedMotion = usePrefersReducedMotion();
  if (!microFeedbackRevealGated(block)) return children;
  if (editor) {
    return (
      <div>
        <div
          data-testid="micro-feedback-reveal-badge"
          style={{
            fontSize: 11,
            marginBottom: 4,
            color: 'var(--color-text-secondary, #8c8c8c)',
          }}
        >
          {pickStrings(authorHintsStrings).microFeedbackRevealBadge}
        </div>
        {children}
      </div>
    );
  }
  const open = isMicroFeedbackRevealed(block, screenBlocks, values);
  // `inert` пока не описан в типах React 18 — атрибут через приведение.
  const inertProps = open
    ? {}
    : ({ inert: '' } as unknown as React.HTMLAttributes<HTMLDivElement>);
  return (
    <div
      data-testid="micro-feedback-reveal"
      data-revealed={open ? 'true' : 'false'}
      aria-hidden={open ? undefined : true}
      style={{
        display: 'grid',
        gridTemplateRows: open ? '1fr' : '0fr',
        opacity: open ? 1 : 0,
        transition: reducedMotion
          ? 'none'
          : `grid-template-rows ${REVEAL_DURATION_MS}ms ${REVEAL_EASING}, opacity ${REVEAL_DURATION_MS}ms ${REVEAL_EASING}`,
      }}
    >
      <div style={{ overflow: 'hidden', minHeight: 0 }} {...inertProps}>
        {children}
      </div>
    </div>
  );
}
